import type { SessionUser } from '@afra/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { type FormEvent, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { RequestError, api } from '../../lib/api'
import { faNum } from '../platform/labels'
import { Rail } from './Rail'
import { ArchiveChat, canArchiveChat } from './ArchiveChat'
import { ConversationText, Transcript } from './Transcript'
import { VoiceInput } from './VoiceInput'
import { AfraMark } from '../../design/AfraMark'

/**
 * The conversation.
 *
 * One input and no forms: the director types whatever they like — a challenge,
 * an answer, a question of their own — and the agent decides what it is. When
 * the agent asks something with a handful of likely answers it offers them as
 * options, which are a shortcut and never a cage: the composer stays, and
 * picking one does not stop somebody adding the sentence that actually matters.
 *
 * What the agent does about any of it is visible beside it in the panel, and
 * below each turn as the tool calls it made. A conversation whose consequences
 * are invisible is one nobody can correct.
 */
export function ChatPage({ user }: { user: SessionUser }) {
  const queryClient = useQueryClient()
  const [draft, setDraft] = useState('')
  const [picked, setPicked] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const bodyRef = useRef<HTMLDivElement>(null)

  const chat = useQuery({ queryKey: ['chat'], queryFn: api.chat })
  const context = useQuery({ queryKey: ['chat-context'], queryFn: api.chatContext })

  /* The turn while it is running: what was just said, the tools the agent ran
     for it, and the reply so far. Replaced by the stored transcript on `done`. */
  const [live, setLive] = useState<LiveTurn | null>(null)

  const say = useMutation({
    mutationFn: (input: { textFa: string; pickedLabelFa: string | null }) => {
      const said = [input.pickedLabelFa?.trim(), input.textFa.trim()].filter(Boolean).join(' — ')
      setLive({ said, textFa: '', tools: [] })
      return api.sayStream(input, {
        delta: (piece) => setLive((t) => (t ? { ...t, textFa: t.textFa + piece } : t)),
        reset: () => setLive((t) => (t ? { ...t, textFa: '' } : t)),
        tool: (tool) => setLive((t) => (t ? { ...t, tools: [...t.tools, tool] } : t)),
      })
    },
    onSettled: () => setLive(null),
    onSuccess: (result) => {
      setError(null)
      setPicked(null)
      /* The server hands back both, so they are set rather than refetched: a
         reply and a panel that disagree for one render is exactly the flicker
         that makes people distrust what the panel says. */
      queryClient.setQueryData(['chat'], { messages: result.messages, showTools: result.showTools })
      queryClient.setQueryData(['chat-context'], result.context)
    },
    onError: (err) => {
      setError(err instanceof RequestError ? err.messageFa : 'پیام فرستاده نشد. دوباره امتحان کنید.')
      /* The server may have stored the message and even the reply before the
         connection went, so what is on screen is re-read rather than guessed. */
      void queryClient.invalidateQueries({ queryKey: ['chat'] })
      void queryClient.invalidateQueries({ queryKey: ['chat-context'] })
    },
  })

  const messages = chat.data?.messages ?? []
  const pending = say.isPending

  /* Options belong to the last thing the agent said, and only while it is still
     the last thing: answering retires them. */
  const lastAgent = [...messages].reverse().find((m) => m.kind === 'message' && m.from === 'agent')
  const lastIsAgent = messages.at(-1)?.from === 'agent'
  const options = lastIsAgent ? (lastAgent?.options ?? null) : null
  const allowCustom = lastAgent?.allowCustom !== false

  // biome-ignore lint/correctness/useExhaustiveDependencies: the scroll follows new messages and the pending state
  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: 'smooth' })
  }, [messages.length, pending, live?.tools.length, live?.textFa.length])

  const send = (event?: FormEvent) => {
    event?.preventDefault()
    const text = draft.trim()
    if (pending) return
    if (!picked && text.length < 2) return
    setDraft('')
    say.mutate({ textFa: text, pickedLabelFa: picked })
  }

  return (
    <div className="chat-layout chat-layout--flush">
      {/* Edge to edge: the chat and the panel are the page (see .main--flush). */}
        <div className="chat">
          <div className="chat__head">
            <div className="chat__avatar" aria-hidden="true">
              <AfraMark />
            </div>
            <div className="chat__identity">
              <div style={{ fontWeight: 700 }}>افرا</div>
              <div className="meta">
                {user.personalSpace
                  ? 'فضای شخصی شما · چیزی از اینجا به هیچ مؤسسه‌ای نمی‌رسد'
                  : `در حال گفت‌وگو با ${user.org.name}`}
              </div>
            </div>
            <Link to="/journey/history" className="btn btn--quiet btn--sm">
              گفت‌وگوهای قبلی
            </Link>
            {/* A plain download link: the server sends the file as an attachment. */}
            <a href="/api/chat/export" className="btn btn--quiet btn--sm" download>
              خروجی HTML
            </a>
            {canArchiveChat(user) && <ArchiveChat user={user} />}
          </div>

          <div className="chat__body" ref={bodyRef}>
            {chat.isLoading && <p className="muted">در حال بارگذاری…</p>}

            <Transcript messages={messages} />

            {live && (
              <>
                <div className="msg msg--org">{live.said}</div>
                {live.tools.map((tool, index) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: the list only grows, in order
                    key={index}
                    className={`tool-line ${tool.failed ? 'tool-line--failed' : ''}`}
                  >
                    <span>{tool.summaryFa}</span>
                    <span className="tool-line__name ltr">{tool.name}</span>
                    {tool.failed && <span className="badge badge--bad">انجام نشد</span>}
                  </div>
                ))}
              </>
            )}

            {pending && (
              <div className="msg msg--agent" aria-live="polite">
                {live?.textFa ? (
                  <ConversationText text={live.textFa} />
                ) : (
                  <span className="typing" aria-label="در حال نوشتن">
                    <span />
                    <span />
                    <span />
                  </span>
                )}
              </div>
            )}
          </div>

          {error && <p className="notice notice--error">{error}</p>}

          {options && options.length > 0 && !pending && (
            <div className="chat__options">
              {options.map((option) => (
                <button
                  key={option.labelFa}
                  type="button"
                  className={`option ${picked === option.labelFa ? 'is-selected' : ''}`}
                  aria-pressed={picked === option.labelFa}
                  onClick={() => setPicked(picked === option.labelFa ? null : option.labelFa)}
                >
                  {option.score !== null && <span className="option__score num">{faNum(option.score)}</span>}
                  <span>{option.labelFa}</span>
                </button>
              ))}
            </div>
          )}

          <form className="chat__composer" onSubmit={send}>
            <VoiceInput
              disabled={pending}
              onText={(textFa) => setDraft((d) => (d.trim() ? `${d.trim()} ${textFa}` : textFa))}
            />
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder={
                picked
                  ? 'اگر توضیحی دارین اینجا اضافه کنین (اختیاری)'
                  : allowCustom
                    ? 'هرچه دوست دارین بنویسین — یک چالش، یک سؤال، یا جواب همین سؤال'
                    : 'یکی از گزینه‌ها را انتخاب کنین'
              }
              aria-label="پیام شما"
              onKeyDown={(e) => {
                // Enter sends, Shift+Enter breaks the line. Persian replies run
                // long and a stray Enter mid-thought must not post half of one.
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  send()
                }
              }}
            />
            <button
              type="submit"
              className="btn"
              disabled={pending || (!picked && draft.trim().length < 2)}
            >
              {pending ? 'صبر کنین…' : 'بفرست'}
            </button>
          </form>
        </div>

        <Rail context={context.data} loading={context.isLoading} />
    </div>
  )
}

interface LiveTurn {
  said: string
  textFa: string
  tools: { name: string; summaryFa: string; failed: boolean }[]
}
